import type React from 'react'
import {useState, useEffect} from 'react'
import {dashboardService} from '@/services/common/dashboardService'
import {updateProgramDashboard} from '@/services/program/programService'
import {clearCacheForEndpoint} from '@/services/interceptors/cacheInterceptor'
import {ENDPOINTS as API_ENDPOINTS} from '@/utils/constants/apiEndpoints'
import type {Column} from '../../../common/DataTable/DataTable'
import DataTable from '../../../common/DataTable/DataTable'
import {Toggle} from 'pepsico-ds'
import LoadingSpinner from '@/components/GDS/atoms/LoadingSpinner'
import ModalWrapper from '@/components/GDS/molecules/Modal/ModalWrapper'
import Toast from '@/components/GDS/molecules/Toast'
import {
  TOAST_MESSAGES,
  MODAL_MESSAGES,
  TABLE_CONFIG,
  TABLE_HEADERS,
  COLUMN_KEYS,
  COLUMN_HEADERS,
  BUTTON_LABELS,
  SEARCH_PLACEHOLDERS,
} from '../constants'
import {createToast} from '../helpers/toastHelpers'
import {isMetricsDataSufficient} from '../helpers/seMetricsValidation'
import {useUrlFilters} from '@/hooks/useUrlFilters'
import {useUrlPagination} from '@/hooks/useUrlPagination'

// Define the Program data type (matching Angular's dashboard structure)
interface Program {
  id: string
  name: string
  ownerName?: string
  ownerDept?: string
  owners?: {ownerName: string}[]
  visibleOnSeMetricsPage: boolean
  componentDashboards?: {name: string}[]
  cloudServiceName?: string
  appDynamicsProject?: string
  fullStoryToken?: string
  leadTimeToChange?: any
  serviceNowConfiguration?: any
}

const ManageSEMetricsKPI: React.FC = () => {
  // URL-based filters and pagination

  const {filters, setFilter} = useUrlFilters()
  const {currentPage, setPage} = useUrlPagination()

  // State for programs
  const [programs, setPrograms] = useState<Program[]>([])

  // Derive state from URL filters
  const searchTerm = filters.search || ''

  // Page size state (not in URL to avoid clutter)
  const [pageSize, setPageSize] = useState<number>(TABLE_CONFIG.DEFAULT_PAGE_SIZE)
  const [sortBy, setSortBy] = useState<string | undefined>(undefined)

  // State for loading
  const [isLoading, setIsLoading] = useState(true)

  // State for total count (server-side pagination)
  const [totalCount, setTotalCount] = useState(0)

  // State for confirmation modal
  const [showConfirmModal, setShowConfirmModal] = useState(false)
  const [selectedProgram, setSelectedProgram] = useState<Program | null>(null)
  const [isEnabling, setIsEnabling] = useState(false)

  // State for toast notifications
  const [toasts, setToasts] = useState<any[]>([])
  const [showToast, setShowToast] = useState(false)

  // Load programs when pagination or search changes
  useEffect(() => {
    loadPrograms()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageSize, searchTerm])

  // Load programs from API with pagination and search
  const loadPrograms = async () => {
    setIsLoading(true)
    try {
      const response = await dashboardService.getProductDashboardsWithParams({
        page: currentPage - 1,
        size: pageSize,
        search: searchTerm,
      })
      setPrograms(response.productDashboards || [])
      setTotalCount(response.total || 0)
    } catch (err) {
      setToasts([createToast(TOAST_MESSAGES.ERROR, TOAST_MESSAGES.LOAD_ERROR, 'error')])
      setShowToast(true)
    } finally {
      setIsLoading(false)
    }
  }

  const refreshPrograms = async () => {
    clearCacheForEndpoint(API_ENDPOINTS.PROGRAMS_LIST_PAGE_FILTER)
    await loadPrograms()
  }

  // Create payload for program visibility update
  const createProgramPayload = (program: Program, isVisible: boolean) => {
    return {
      configurationItem: program.name,
      configurationItemType: 'APP',
      appServiceOwner: program.ownerName,
      businessOwner: program.ownerName,
      ownerDept: program.ownerDept,
      owners: program.owners,
      supportOwner: program.ownerName,
      developmentOwner: program.ownerName,
      itemType: 'APP',
      components: program.componentDashboards?.map(dashboardItem => dashboardItem.name) || [],
      cloudService: program.cloudServiceName,
      appDynamicsProject: program.appDynamicsProject,
      fullStoryProject: program.fullStoryToken,
      leadTimeToChangeConfiguration: program.leadTimeToChange,
      serviceNowConfiguration: program.serviceNowConfiguration,
      isVisibleOnSeMetricsPage: isVisible,
    }
  }

  // Handle toggle change
  const handleToggleChange = (program: Program) => {
    if (program.visibleOnSeMetricsPage) {
      // If currently enabled, disable directly
      disableProgram(program)
    } else {
      // If currently disabled, check data before enabling
      checkDataAndEnableProgram(program)
    }
  }

  // Check if program has sufficient data before enabling (same as Angular's viewDashboard)
  const checkDataAndEnableProgram = (program: Program) => {
    // setIsPaginationLoading(true)

    // Get current month and year
    const currentMonth = new Date().getMonth() + 1
    const currentYear = new Date().getFullYear()

    // Call the actual API to fetch metrics data (same as Angular's fetchSeMetricsOverallData)
    dashboardService
      .getSEMetrics({
        productDashboardId: program.id,
        month: currentMonth,
        year: currentYear,
      })
      .then(metricsData => {
        // Check if data is sufficient
        const isDataSufficient = isMetricsDataSufficient(metricsData)

        if (isDataSufficient) {
          // If data is sufficient, enable directly and show success (like Angular's openSuccessModal)
          enableProgramDirectly(program)
        } else {
          // If data is insufficient, show confirmation modal (like Angular's openConfirmationModal)
          setSelectedProgram(program)
          setIsEnabling(true)
          setShowConfirmModal(true)
        }
      })
      .catch((err: any) => {
        setToasts([createToast(TOAST_MESSAGES.ERROR, TOAST_MESSAGES.PROGRAM_DATA_CHECK_ERROR, 'error')])
        setShowToast(true)
      })
  }

  // Enable program directly when data is sufficient
  const enableProgramDirectly = (program: Program) => {
    const submitData = createProgramPayload(program, true)

    // Update program visibility
    updateProgramDashboard(program.id, submitData)
      .then(() => {
        // Show success message
        setToasts([
          createToast(TOAST_MESSAGES.PROGRAM_ENABLED, TOAST_MESSAGES.PROGRAM_ENABLED_SUCCESS(program.name), 'success'),
        ])
        setShowToast(true)

        // Refresh from backend to keep UI in sync with latest source of truth
        refreshPrograms()
      })
      .catch((err: any) => {
        setToasts([createToast(TOAST_MESSAGES.ERROR, TOAST_MESSAGES.PROGRAM_ENABLE_ERROR, 'error')])
        setShowToast(true)
      })
  }

  const disableProgram = (program: Program) => {
    try {
      const submitData = createProgramPayload(program, false)

      // Update program visibility using the actual API
      updateProgramDashboard(program.id, submitData)
        .then(() => {
          // Show success message
          setToasts([
            createToast(
              TOAST_MESSAGES.PROGRAM_DISABLED,
              TOAST_MESSAGES.PROGRAM_DISABLED_SUCCESS(program.name),
              'success',
            ),
          ])
          setShowToast(true)

          // Refresh from backend to keep UI in sync with latest source of truth
          refreshPrograms()
        })
        .catch((err: any) => {
          setToasts([createToast(TOAST_MESSAGES.ERROR, TOAST_MESSAGES.PROGRAM_DISABLE_ERROR, 'error')])
          setShowToast(true)
        })
    } catch (err) {
      setToasts([createToast(TOAST_MESSAGES.ERROR, TOAST_MESSAGES.UNEXPECTED_ERROR, 'error')])
      setShowToast(true)
    }
  }

  // Confirm enable program despite insufficient data (user confirmed in modal)
  const confirmEnableProgram = () => {
    if (!selectedProgram) return

    const submitData = createProgramPayload(selectedProgram, true)

    // Update program visibility (user already confirmed despite insufficient data)
    updateProgramDashboard(selectedProgram.id, submitData)
      .then(() => {
        // Show success message
        setToasts([
          createToast(
            TOAST_MESSAGES.PROGRAM_ENABLED,
            TOAST_MESSAGES.PROGRAM_ENABLED_SUCCESS(selectedProgram.name),
            'success',
          ),
        ])
        setShowToast(true)

        // Close modal
        setShowConfirmModal(false)
        setSelectedProgram(null)

        // Refresh from backend to keep UI in sync with latest source of truth
        refreshPrograms()
      })
      .catch((err: any) => {
        setToasts([createToast(TOAST_MESSAGES.ERROR, TOAST_MESSAGES.PROGRAM_ENABLE_ERROR, 'error')])
        setShowToast(true)
      })
  }

  // Handle global search change - triggers API call
  const handleGlobalSearchChange = (value: string) => {
    setFilter('search', value || undefined)
    // Search is handled by DataTable's debounced onGlobalSearchChange
    // useUrlFilters automatically resets pagination when filters change
  }

  // Handle page change
  const handlePageChange = (newPageIndex: number) => {
    setPage(newPageIndex)
  }

  // Handle rows per page change
  const handleRowsPerPageChange = (newPageSize: number) => {
    if (newPageSize !== pageSize) {
      setPage(1)
      setPageSize(newPageSize)
    }
  }

  // Define columns for the DataTable
  const columns: Column[] = [
    {
      columnKey: COLUMN_KEYS.NAME,
      header: COLUMN_HEADERS.PROGRAM_NAME,
      initialSize: TABLE_CONFIG.SE_METRICS_COLUMN_SIZES.PROGRAM_NAME,
      cellData: ({row}) => (
        <div
          className="flex w-full items-center justify-between"
          style={{
            display: 'flex',
            minHeight: 'var(--Spacing-spacing-40, 40px)',
            padding: 'var(--Spacing-spacing-8, 8px) 0',
            alignItems: 'center',
            alignSelf: 'stretch',
          }}
        >
          <div
            className="flex-1 font-[Inter] text-sm leading-5 font-normal text-[var(--color-text-primary)]"
            style={{
              display: 'flex',
              padding: '0 var(--Spacing-spacing-8, 8px)',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'flex-start',
              gap: '-2px',
              flex: '1 0 0',
            }}
          >
            {row.original.name}
          </div>
        </div>
      ),
    },
    {
      columnKey: COLUMN_KEYS.ACTION,
      header: COLUMN_HEADERS.ACTION,
      initialSize: TABLE_CONFIG.SE_METRICS_COLUMN_SIZES.ACTION,
      cellData: ({row}) => (
        <div
          className="justify-begin flex w-full items-center"
          style={{
            display: 'flex',
            minHeight: 'var(--Spacing-spacing-40, 40px)',
            padding: 'var(--Spacing-spacing-8, 8px) 0',
            alignItems: 'center',
            alignSelf: 'stretch',
          }}
        >
          <div
            className="justify-begin flex items-center gap-4"
            style={{
              display: 'flex',
              padding: '0 var(--Spacing-spacing-8, 8px)',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'flex-end',
              gap: '-2px',
            }}
          >
            <Toggle
              className=""
              value={row.original.visibleOnSeMetricsPage ? 'Active' : 'Inactive'}
              size="medium"
              style={{}}
              checked={row.original.visibleOnSeMetricsPage}
              onUpdate={() => handleToggleChange(row.original)}
              variant="horizontal"
            />
          </div>
        </div>
      ),
    },
  ]
  return (
    <div className="w-full p-6">
      {/* Loading state */}
      {isLoading && searchTerm === '' ? (
        <div className="flex h-screen w-full items-center justify-center">
          <LoadingSpinner size={40} />
        </div>
      ) : (
        <div className="flex w-full flex-1 flex-col items-start gap-4">
          <>
            {searchTerm !== '' && isLoading && (
              <div className="flex w-full items-center justify-center py-4">
                <LoadingSpinner size={40} />
              </div>
            )}
            <DataTable
              className="w-full [&_tbody_tr]:min-h-[40px]"
              viewPort="Desktop"
              style="naked"
              data={programs}
              columns={columns}
              enableSorting={false}
              hideHeader={false}
              hideTableToolbar={true}
              showSearchOption={true}
              searchPlaceholder={SEARCH_PLACEHOLDERS.PROGRAMS}
              onGlobalSearchChange={handleGlobalSearchChange}
              showDescription={true}
              tableHeader={TABLE_HEADERS.MANAGE_SE_METRICS}
              tableDescription={TABLE_HEADERS.MANAGE_SE_METRICS_DESC}
              enableTextSelection={true}
              size="medium"
              pageSize={pageSize}
              pageIndex={currentPage - 1}
              onPageIndexChange={handlePageChange}
              onRowsPerPageChange={handleRowsPerPageChange}
              isLoading={isLoading}
              manualPagination={true}
              totalRowsCount={totalCount}
              forceShowPagination={true}
              pageSizeOptions={TABLE_CONFIG.PAGE_SIZE_OPTIONS}
              debounceDelay={500}
            />
          </>
        </div>
      )}

      {/* Toast notifications */}
      {showToast && (
        <Toast
          toasts={toasts}
          position="bottom-center"
          removeToast={(id: any) => setToasts(prev => prev.filter(t => t.id !== id))}
          showActionIcon
        />
      )}

      {/* Confirmation Modal for Enabling Program */}
      <ModalWrapper
        showModal={showConfirmModal}
        showSlot={true}
        onCloseModal={() => {
          setShowConfirmModal(false)
          setSelectedProgram(null)
          refreshPrograms()
        }}
        title={MODAL_MESSAGES.ENABLE_PROGRAM_TITLE}
        text=""
        primaryButtonProps={{
          text: BUTTON_LABELS.ENABLE,
          onClick: confirmEnableProgram,
          variant: 'primary',
        }}
        secondaryButtonProps={{
          text: BUTTON_LABELS.CANCEL,
          onClick: () => {
            setShowConfirmModal(false)
            setSelectedProgram(null)
            refreshPrograms()
          },
          variant: 'secondary',
        }}
        closeOnOutsideClick={false}
      >
        <div className="p-4">
          <p className="text-[var(--color-text-secondary)]">{MODAL_MESSAGES.ENABLE_PROGRAM_INSUFFICIENT_DATA_PREFIX}</p>
          <p className="mt-2 text-[var(--color-text-secondary)]">
            {MODAL_MESSAGES.ENABLE_PROGRAM_INSUFFICIENT_DATA_SUFFIX(selectedProgram?.name || '')}
          </p>
        </div>
      </ModalWrapper>
    </div>
  )
}

export default ManageSEMetricsKPI
